















import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/AdminEquipment.css';

const AdminEquipment = () => {
  const navigate = useNavigate();
  
  // State for equipment
  const [equipment, setEquipment] = useState([]);
  
  // State for categories
  const [categories, setCategories] = useState([]);
  
  // State for loading status
  const [loading, setLoading] = useState(true);
  
  // State for category attributes
  const [categoryAttributes, setCategoryAttributes] = useState([]);
  
  // State for form data
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    description: '',
    measurement: '',
    attribute_values: {}
  });
  
  // State for form visibility
  const [showForm, setShowForm] = useState(false);
  
  // State for editing
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  
  // State for selected category filter
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // State for messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5; // Show 5 items per page
  
  // API URL
  const apiUrl = 'https://ecl.net.in/backende';

  // Fetch categories callback
  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/equipment-categories`);
      // Ensure categories is always an array
      const categoriesData = Array.isArray(response.data) ? response.data : [];
      setCategories(categoriesData);
      
      // Set default category for the form if not already set
      if (categoriesData.length > 0 && !formData.category_id) {
        setFormData(prev => ({
          ...prev,
          category_id: categoriesData[0].id
        }));
        
        // Fetch attributes for the first category
        fetchCategoryAttributes(categoriesData[0].id);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setErrorMessage('Failed to load categories data. ' + (error.response?.data?.message || error.message));
      setCategories([]); // Set to empty array on error
    }
  }, [formData.category_id]);

  // Fetch equipment callback - FIXED
  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/equipment`);
      
      // Ensure equipment is always an array
      if (response.data && response.data.equipment) {
        setEquipment(Array.isArray(response.data.equipment) ? response.data.equipment : []);
      } else if (Array.isArray(response.data)) {
        // Direct array response
        setEquipment(response.data);
      } else {
        console.warn('Unexpected response format:', response.data);
        setEquipment([]);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching equipment:', error);
      setErrorMessage('Failed to load equipment data. ' + (error.response?.data?.message || error.message));
      setEquipment([]); // Set to empty array on error
      setLoading(false);
    }
  }, []);

  // Function to fetch category attributes
  const fetchCategoryAttributes = async (categoryId) => {
    try {
      const response = await axios.get(`${apiUrl}/api/equipment-attributes/category/${categoryId}`);
      // Ensure attributes is always an array
      const attributesData = Array.isArray(response.data) ? response.data : [];
      setCategoryAttributes(attributesData);
    } catch (error) {
      console.error('Error fetching category attributes:', error);
      setCategoryAttributes([]);
    }
  };

  // Check admin status and load data on component mount
  useEffect(() => {
    // Check if user is admin
    const isAdmin = sessionStorage.getItem('isAdmin');
    if (isAdmin !== 'true') {
      navigate('/login');
      return;
    }
    
    fetchCategories();
    fetchEquipment();
  }, [navigate, fetchCategories, fetchEquipment]);

  // Handle category change
  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      // Reset attribute values when category changes
      attribute_values: {}
    }));
    
    // Fetch attributes for the selected category
    fetchCategoryAttributes(categoryId);
  };

  // Handle form submission - FIXED
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Create a FormData object to send the data
      const formDataToSend = new FormData();
      
      // Add basic equipment fields to the FormData
      formDataToSend.append('name', formData.name);
      formDataToSend.append('category_id', formData.category_id);
      
      if (formData.measurement) {
        formDataToSend.append('measurement', formData.measurement);
      }
      
      if (formData.description) {
        formDataToSend.append('description', formData.description);
      }
      
      let response;
      if (editMode) {
        // Update existing equipment
        response = await axios.put(
          `${apiUrl}/api/equipment/${editId}`,
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        // Create new equipment
        response = await axios.post(
          `${apiUrl}/api/equipment`, 
          formDataToSend,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }
      
      if (response.data && response.data.success) {
        setSuccessMessage(editMode ? 'Equipment updated successfully!' : 'Equipment added successfully!');
        resetForm();
        fetchEquipment(); // Refresh the list
      } else {
        setErrorMessage((response.data && response.data.message) || 'Failed to save equipment');
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (error) {
      console.error('Error saving equipment:', error);
      setErrorMessage('Failed to save equipment: ' + (error.response?.data?.message || error.message));
      
      // Clear error message after 3 seconds
      setTimeout(() => {
        setErrorMessage('');
      }, 3000);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle category filter change
  const handleCategoryFilterChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1); // Reset to first page when changing filters
  };

  // Handle edit button click
  const handleEdit = (item) => {
    // Find category for this item
    const category = categories.find(cat => cat.name === item.category_name);
    if (!category) {
      console.error('Category not found for item:', item);
      setErrorMessage('Category not found for this equipment');
      return;
    }
    
    setFormData({
      name: item.name,
      category_id: category.id.toString(), // Ensure this is a string for consistent comparison
      description: item.description || '',
      measurement: item.measurement || '',
      attribute_values: {} // We're not using attribute values
    });
    
    // Fetch attributes for this category
    fetchCategoryAttributes(category.id);
    
    setEditMode(true);
    setEditId(item.id);
    setShowForm(true);
  };

  // Handle delete button click
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this equipment?')) {
      try {
        const response = await axios.delete(`${apiUrl}/api/equipment/${id}`);
        
        if (response.data && response.data.success) {
          setSuccessMessage('Equipment deleted successfully!');
          fetchEquipment(); // Refresh the list
        } else {
          setErrorMessage((response.data && response.data.message) || 'Failed to delete equipment');
        }
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } catch (error) {
        console.error('Error deleting equipment:', error);
        setErrorMessage('Failed to delete equipment: ' + (error.response?.data?.message || error.message));
        
        // Clear error message after 3 seconds
        setTimeout(() => {
          setErrorMessage('');
        }, 3000);
      }
    }
  };

  // Reset form
  const resetForm = () => {
    const initialCategoryId = categories.length > 0 ? categories[0].id : '';
    
    setFormData({
      name: '',
      category_id: initialCategoryId,
      description: '',
      measurement: '',
      attribute_values: {}
    });
    
    if (initialCategoryId) {
      fetchCategoryAttributes(initialCategoryId);
    }
    
    setEditMode(false);
    setEditId(null);
    setShowForm(false);
  };

  // Filter equipment based on search term and selected category - FIXED WITH USEMEMO
  const filteredEquipment = useMemo(() => {
    // Make sure equipment is an array before filtering
    if (!Array.isArray(equipment)) {
      console.warn('Equipment is not an array:', equipment);
      return [];
    }
    
    return equipment.filter(item => {
      // Filter by search term
      const matchesSearch = !searchTerm || 
        (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Filter by category
      const matchesCategory = selectedCategory === 'all' || 
        (item.category_name && item.category_name.toLowerCase() === selectedCategory.toLowerCase());
      
      return matchesSearch && matchesCategory;
    });
  }, [equipment, searchTerm, selectedCategory]);
  
  // Calculate total pages
  const totalPages = Math.ceil(filteredEquipment.length / itemsPerPage);
  
  // Get current items
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredEquipment.slice(indexOfFirstItem, indexOfLastItem);
  
  // Change page
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  // Previous page
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  
  // Next page
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Toggle form visibility
  const toggleForm = () => {
    setShowForm(!showForm);
    if (showForm) {
      resetForm();
    }
  };

  // Get the current category for the form
  const currentCategory = categories.find(cat => cat.id === parseInt(formData.category_id, 10));

  if (loading) {
    return <div className="admin-equipment loading-container">Loading equipment data...</div>;
  }

  return (
    <div className="admin-equipment">
      <div className="admin-page-header">
        <h1>Equipment Management</h1>
        <p>Manage equipment inventory and specifications</p>
      </div>

      {/* Success and Error Messages */}
      {successMessage && <div className="success-message">{successMessage}</div>}
      {errorMessage && <div className="error-message">{errorMessage}</div>}

      {/* Equipment Form */}
      {showForm && (
        <div className="equipment-form-container">
          <h2>{editMode ? 'Edit Equipment' : 'Add New Equipment'}</h2>
          
          <form onSubmit={handleSubmit} className="equipment-form">
            <div className="form-row">
              {/* Left side - Category Selection */}
              <div className="form-column">
                <div className="form-group">
                  <label htmlFor="category_id">Category *</label>
                  {editMode ? (
                    // Read-only display when editing
                    <div className="readonly-field">
                      {categories.find(cat => cat.id.toString() === formData.category_id.toString())?.name || 'Unknown Category'}
                    </div>
                  ) : (
                    // Regular dropdown when adding new equipment
                    <select
                      id="category_id"
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleCategoryChange}
                      required
                      className="dropdown-select"
                    >
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>{category.name}</option>
                      ))}
                    </select>
                  )}
                  
                  {/* Hidden input to maintain the value in form submission */}
                  <input 
                    type="hidden" 
                    name="category_id" 
                    value={formData.category_id} 
                  />
                </div>
              </div>
            </div>

            {/* Display Category Attributes */}
            {categoryAttributes && categoryAttributes.length > 0 ? (
              <div className="form-row attributes-row">
                {categoryAttributes.map(attr => (
                  <div className="attribute-display" key={attr.id}>
                    <div className="attribute-info">
                      <label>Attribute Name:</label>
                      <span className="attribute-value">{attr.name}</span>
                    </div>
                    <div className="attribute-info">
                      <label>Measurement Name:</label>
                      <span className="attribute-value">{attr.display_name}</span>
                    </div>
                    {attr.description && (
                      <div className="attribute-info">
                        <label>Description:</label>
                        <span className="attribute-value">{attr.description}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-attributes-message">
                No attributes found for this category.
              </div>
            )}

            {/* Equipment Details */}
            <div className="equipment-details-container">
              <div className="form-group">
                <label htmlFor="name">Equipment Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter equipment name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="measurement">
                  {currentCategory && currentCategory.measurement_label 
                    ? currentCategory.measurement_label 
                    : "Equipment Measurement"}
                </label>
                <input
                  type="text"
                  id="measurement"
                  name="measurement"
                  value={formData.measurement}
                  onChange={handleInputChange}
                  placeholder="Enter measurement details"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Optional description"
                  rows="3"
                />
              </div>
            </div>

            <div className="form-buttons">
              <button type="submit" className="save-button">
                {editMode ? 'Update Equipment' : 'Save Equipment'}
              </button>
              <button type="button" className="cancel-button" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Equipment Button and Search */}
      <div className="admin-controls">
        <button className="add-equipment-btn" onClick={toggleForm}>
          + Add New Equipment
        </button>
        
        <div className="equipment-filters">
          <div className="category-filter">
            <select 
              className="dropdown-select"
              value={selectedCategory} 
              onChange={handleCategoryFilterChange}
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.name}>{category.name}</option>
              ))}
            </select>
          </div>
          
          <div className="search-filter">
            <input
              type="text"
              placeholder="Search equipment..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Equipment Table */}
      <table className="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Equipment Name</th>
            <th>Category</th>
            <th>Equipment Measurement</th>
            <th>Description</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.length > 0 ? (
            currentItems.map(item => (
              <tr key={item.id}>
                <td>{item.id}</td>
                <td>{item.name || '-'}</td>
                <td>{item.category_name || '-'}</td>
                <td>{item.measurement || '-'}</td>
                <td>{item.description || '-'}</td>
                <td className="action-buttons">
                  <button className="edit-btn" onClick={() => handleEdit(item)}>
                    Edit
                  </button>
                  <button className="delete-btn" onClick={() => handleDelete(item.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="no-results">No equipment found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {filteredEquipment.length > 0 && (
        <div className="pagination">
          <button 
            onClick={handlePreviousPage} 
            disabled={currentPage === 1}
          >
            &lt; Previous
          </button>
          <div className="page-numbers">
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index + 1}
                className={currentPage === index + 1 ? 'active' : ''}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </button>
            ))}
          </div>
          <button 
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next &gt;
          </button>
        </div>
      )}

      {/* Back Link */}
      {/* <div className="back-link">
        <Link to="/admin/master-equipment">← Back to Master Equipment</Link>
      </div> */}
    </div>
  );
};

export default AdminEquipment;